/*
  Sent.js - Component to handle Sent SMS content
*/

// NPM
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Stack,
  Typography,
  Grid,
} from '@mui/material';

// COMPONENTS
import SentSingle from '../../components/websms/sent/SentSingle';
import Paginator from '../../components/common/Paginator';
import DateRangeSelector from '../../components/common//DateRangeSelector';
import SectionWrapper from '../../components/common/SectionWrapper';
import ConfirmationDialog from '../../components/websms/sent/ConfirmationDialog';
import AdvancedFiltersContainer from '../../components/common/AdvancedFiltersContainer';
import DataExportModal from '../../components/common/modals/DataExportModal';

// LAYOUT
import Layout from '../../components/layouts/Layout';

// ROUTES
import { WebsmsRoutes } from '../../routes/WebsmsRoutes';

// MESSAGES
import SentFormattedMessages from '../../messages/websms/SentFormattedMessages';

// CONFIG
import config from '../../Config';

// NETWORK
import useSentRequest from '../../requests/SentRequest';

// UTILS
import SentTable from '../../components/websms/sent/SentTable';

const Sent = ({ location, errorHandler }) => {
  const [sent, setSent] = useState([]);
  const [messageSelected, setMessageSelected] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [fromFilter, setFromFilter] = useState('');
  const [toFilter, setToFilter] = useState('');
  const [timeRange, setTimeRange] = useState(location?.state?.period || null);
  const [nameFilter, setNameFilter] = useState('');
  const [messageFilter, setMessageFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(
    location?.state?.status || ''
  );
  const [type, setType] = useState('TXT');
  const [isSearching, setIsSearching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageLength, setPageLength] = useState(config.defaultPL);
  const [currentlySearching, setCurrentlySearching] = useState({});
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [selectorKeyRefresh, setSelectorKeyRefresh] = useState(false);
  const [cancelList, setCancelList] = useState(new Set());
  const [filteredText, setFilteredText] = useState([]);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [showDataExportModal, setShowDataExportModal] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);

  const {
    searchSentItems,
    cancelAllPendingMessages,
    requestExportSentMessages,
  } = useSentRequest();

  const sendStatus = [
    'Blacklisted',
    'Delivered',
    'Failed Delivery',
    'Invalid Number',
    'Presumed Delivered',
    'Processing',
    'Too Many Retries',
  ];

  const handleBackButton = () => {
    setMessageSelected(false);
    setMessage(null);
    setCancelList(new Set());
  };

  const handleInputChange = (setter) => (event) => {
    setter(event.target.value);
  };

  const handleUserDataSearch = async (fromDate, toDate, timeRange) => {
    try {
      setIsSearching(true);
      setSent([]);

      const params = {
        fromDate,
        toDate,
        timeRangeStart: fromDate,
        timeRangeEnd: toDate,
        fromFilter,
        toFilter,
        nameFilter,
        messageFilter,
        statusFilter,
        type: 'TXT',
        timeRange,
        pageNumber,
        pageLength,
      };
      setSearchParams(params);

      const response = await searchSentItems(params, errorHandler);

      setSent(response.items);
      setTimeRange(timeRange);
      setCurrentlySearching(params);
      setRowCount(response.rowCount);
      setIsSearching(false);
      setCancelList(new Set());
      generateSearchText();
    } catch (e) {
      console.error(e);
      setIsSearching(false);
      setCancelList(new Set());
      setError(e.message);
    }
  };

  const exportSentMessages = async () => {
    const response = await requestExportSentMessages(
      currentlySearching,
      errorHandler
    );
    console.log('response:', response);
    setEmailRecipient(response);
    setShowDataExportModal(true);
  };

  const handleCancelAllPendingMessages = async () => {
    setOpenConfirmationDialog(false);
    try {
      await cancelAllPendingMessages(Array.from(cancelList), errorHandler);
      setSelectorKeyRefresh(!selectorKeyRefresh);
      setCancelList(new Set());
      handleUserDataSearch(fromDate, toDate);
    } catch (e) {
      errorHandler(e.message);
    }
  };

  const handleShowConfirmation = () => {
    setOpenConfirmationDialog(!openConfirmationDialog);
  };

  const handleCancelList = (smsId, remove) => {
    const newCancelList = new Set(cancelList);
    if (remove) newCancelList.delete(smsId);
    else newCancelList.add(smsId);
    setCancelList(newCancelList);
  };

  const generateSearchText = () => {
    if (message) return null;

    const filterText = [];
    const filterValues = ['FROM:', 'TO:', 'NAME:', 'MESSAGE:', 'STATUS:'];
    [fromFilter, toFilter, nameFilter, messageFilter, statusFilter].forEach(
      (filter, i) => {
        if (filter !== '')
          filterText.push(
            <span key={i}>
              {filterText && filterText.length === 0 ? '' : ', '}
              <strong>{filterValues[i]}</strong> {filter}
            </span>
          );
      }
    );

    setFilteredText(filterText);
  };

  const changePageLength = (e) => {
    const value = e.target.value;
    setPageLength(value);
    setPageNumber(1);
  };

  const handleCheckboxChange = (event, id) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;

    handleCancelList(id, !value);
  };

  const changePageEvent = (page) => {
    setPageNumber(page);
  };

  const handlePageReset = () => {
    setMessage(null);
    setSent([]);
    setSelectorKeyRefresh(!selectorKeyRefresh);
  };

  const clearFilters = () => {
    setFromFilter('');
    setToFilter('');
    setNameFilter('');
    setMessageFilter('');
    setStatusFilter('');
    document.getElementById('filter-form').reset();
    setFilteredText('');
    setPageNumber(1);
    handlePageReset();
  };

  // Open advanced filters if passed to page
  useEffect(() => {
    if (location?.state?.status) setOpenFilters(true);
  }, []);

  useEffect(() => {
    if (fromDate && toDate) handleUserDataSearch(fromDate, toDate, timeRange);
  }, [fromDate, toDate, pageNumber, pageLength]);

  const generateExportButton = () => {
    let message = 'Export';
    return (
      <Box>
        <Button
          variant="outlined"
          color="secondary"
          type="submit"
          onClick={exportSentMessages}
        >
          {message}
        </Button>
      </Box>
    );
  };

  const generateCancelPendingButton = () => {
    let message = 'Cancel All Pending Messages';
    if (cancelList.size > 0) message = 'Cancel Selected Pending Messages';
    return (
      <Box>
        <Button
          variant="outlined"
          color="secondary"
          type="submit"
          onClick={handleShowConfirmation}
        >
          {message}
        </Button>
      </Box>
    );
  };

  const showConfirmationDialog = () => {
    let modalMessage =
      'Are you sure you would like to cancel all of your pending messages?';
    if (cancelList.size > 0)
      modalMessage = `Are you sure you would like to cancel ${cancelList.size} message(s)`;

    return (
      <ConfirmationDialog
        showConfirmation={openConfirmationDialog}
        setShowConfirmation={setOpenConfirmationDialog}
        handleCloseConfirmation={handleShowConfirmation}
        message={modalMessage}
        handleCancelPendingMessage={handleCancelAllPendingMessages}
      />
    );
  };

  return (
    <Layout routes={WebsmsRoutes} title={SentFormattedMessages.sentHeader}>
      {messageSelected ? (
        <SentSingle
          sms={message}
          handleBackButton={handleBackButton}
          errorHandler={errorHandler}
        />
      ) : (
        <Grid container sx={{ justifyContent: 'space-between', rowGap: 2 }}>
          <Grid size={{ xs: 12, lg: 9 }}>
            <SectionWrapper
              title="Search Sent Messages"
              icon="search"
              sx={{ maxWidth: 1000 }}
            >
              <DateRangeSelector
                key={selectorKeyRefresh ? 1 : 0}
                handleDateSearch={handleUserDataSearch}
                parentStartDate={fromDate}
                setParentStartDate={setFromDate}
                parentEndDate={toDate}
                setParentEndDate={setToDate}
              />

              <AdvancedFiltersContainer
                setOpen={setOpenFilters}
                open={openFilters}
              >
                <Box component="form" id="filter-form">
                  <Stack
                    sx={{
                      flexDirection: { lg: 'row' },
                      gap: 2,
                      marginY: 2,
                      maxWidth: { lg: 674 },
                    }}
                  >
                    <Stack>
                      <Typography sx={{ whiteSpace: 'nowrap', marginTop: 1 }}>
                        By Field:
                      </Typography>
                    </Stack>

                    <Stack sx={{ gap: 1, flex: 1 }}>
                      <Stack
                        sx={{
                          flexDirection: { lg: 'row' },
                          gap: 2,
                          alignItems: { lg: 'center' },
                          marginBottom: { xs: 1, lg: 0 },
                        }}
                      >
                        <TextField
                          label="From"
                          value={fromFilter}
                          onChange={handleInputChange(setFromFilter)}
                          size="small"
                          fullWidth
                        />
                        <TextField
                          label="To"
                          value={toFilter}
                          onChange={handleInputChange(setToFilter)}
                          size="small"
                          fullWidth
                        />
                        <TextField
                          label="Name"
                          value={nameFilter}
                          onChange={handleInputChange(setNameFilter)}
                          size="small"
                          fullWidth
                        />
                      </Stack>

                      <Typography
                        fontSize={12}
                        sx={{ color: 'grey.600', order: { lg: 0, xs: -1 } }}
                      >
                        {SentFormattedMessages.filterNote}
                      </Typography>

                      <Stack>
                        <Stack
                          sx={{
                            flexDirection: { lg: 'row' },
                            gap: 2,
                            alignItems: { lg: 'center' },
                          }}
                        >
                          <TextField
                            label="Message"
                            value={messageFilter}
                            onChange={handleInputChange(setMessageFilter)}
                            size="small"
                            fullWidth
                          />

                          <TextField
                            fullWidth
                            variant="outlined"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            select
                            size="small"
                            label="Status"
                          >
                            <MenuItem value="">&nbsp;</MenuItem>
                            {sendStatus.map((status) => (
                              <MenuItem key={status} value={status}>
                                {status}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Stack>
                </Box>
              </AdvancedFiltersContainer>

              <Stack
                sx={{
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                {filteredText && filteredText.length !== 0 && (
                  <Button
                    color="secondary"
                    onClick={clearFilters}
                    sx={{
                      textTransform: 'none',
                      textDecoration: 'underline',
                      fontWeight: 400,
                    }}
                  >
                    {SentFormattedMessages.clearFilters}
                  </Button>
                )}

                <Button
                  onClick={() => {
                    setPageNumber(1);
                    handlePageReset();
                  }}
                  color="secondary"
                  variant="outlined"
                >
                  {SentFormattedMessages.searchButton}
                </Button>
              </Stack>
            </SectionWrapper>
          </Grid>

          <Grid size={12}>
            <Stack
              sx={{
                flexDirection: 'row',
                justifyContent:
                  filteredText && filteredText.length !== 0
                    ? 'space-between'
                    : 'flex-end',
                alignItems: 'flex-end',
                marginBottom: 1,
              }}
            >
              {filteredText && filteredText.length !== 0 && (
                <Typography fontSize={12}>{filteredText}</Typography>
              )}
              <Stack direction="row" spacing={2}>
                {generateExportButton()}
                {generateCancelPendingButton()}
              </Stack>
            </Stack>

            <SentTable
              sent={sent}
              isSearching={isSearching}
              setMessageSelected={setMessageSelected}
              setMessage={setMessage}
              handleCheckboxChange={handleCheckboxChange}
            />

            <Paginator
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              itemCount={rowCount ?? 0}
              changeEvent={changePageEvent}
              pageLength={pageLength}
              editFunction={(e) => changePageLength(e)}
              showTotal={'Found'}
              toolTip={'Maximum of 4,000 records shown'}
            />
          </Grid>
        </Grid>
      )}
      {showConfirmationDialog()}
      <DataExportModal
        show={showDataExportModal}
        onHide={() => setShowDataExportModal(false)}
        emailRecipient={emailRecipient}
      />
    </Layout>
  );
};

export default Sent;
